/**
 * Swap Order Status Check Utility
 *
 * Checks if a swap_order PDA has already been processed on-chain
 * to prevent duplicate transaction submissions.
 */

import SHA256 from 'crypto-js/sha256';
import { Connection, PublicKey } from '@solana/web3.js';

import config from '@/config';

const SWAP_ORDER_SEED = 'swap_order';

/**
 * Derive the swap_order PDA from an orderId
 *
 * Uses SHA-256 hash of orderId as seed, matching the backend implementation
 * in junkfun-backend/src/biz-modules/tx-order/signatures/svm/util.ts
 *
 * @param orderId - Unique order identifier from backend
 */
export function deriveSwapOrderPDA(orderId: string): PublicKey {
  // Swap operations now use main program
  const programId = new PublicKey(config.SOLANA.PROGRAM.JUNK_FUN_PROGRAM_ID);
  const hexString = SHA256(orderId).toString();
  const swapOrderSeed = Uint8Array.from(Buffer.from(hexString, 'hex'));

  console.log(`[deriveSwapOrderPDA] orderId: ${orderId}`);
  console.log(`[deriveSwapOrderPDA] SHA256 hex: ${hexString}`);
  console.log(`[deriveSwapOrderPDA] programId: ${programId.toString()}`);

  const [pda] = PublicKey.findProgramAddressSync(
    [Buffer.from(SWAP_ORDER_SEED), swapOrderSeed],
    programId
  );

  console.log(`[deriveSwapOrderPDA] Derived PDA: ${pda.toString()}`);

  return pda;
}

/**
 * Check if a swap order has already been executed on-chain
 *
 * This is used to detect when a transaction succeeded on-chain but
 * confirmation timed out. If the order is already used, we can skip
 * re-sending the transaction.
 *
 * Returns:
 * - { exists: false, isUsed: false } - Order doesn't exist yet
 * - { exists: true, isUsed: false } - Order exists but not yet wrapped up
 * - { exists: true, isUsed: true } - Order already wrapped up (consumed)
 *
 * @param connection - Solana connection
 * @param orderId - Order ID to check
 */
export async function checkSwapOrderStatus(
  connection: Connection,
  orderId: string
): Promise<{ exists: boolean; isUsed: boolean }> {
  const swapOrderPDA = deriveSwapOrderPDA(orderId);

  console.log(`[checkSwapOrderStatus] Checking order: ${orderId}`);
  console.log(`[checkSwapOrderStatus] Derived PDA: ${swapOrderPDA.toString()}`);

  try {
    const accountInfo = await connection.getAccountInfo(swapOrderPDA);

    console.log(`[checkSwapOrderStatus] Account info:`, {
      exists: !!accountInfo,
      dataLength: accountInfo?.data?.length,
      owner: accountInfo?.owner?.toString(),
    });

    if (!accountInfo || !accountInfo.data) {
      console.log(`[checkSwapOrderStatus] Account does not exist`);
      return { exists: false, isUsed: false };
    }

    // SwapOrder account structure (from program/programs/junk-fun-program/src/states/swap_order.rs):
    // - 8 bytes: Anchor discriminator
    // - 8 bytes: user_info_before_balance (u64)
    //
    // When wrapUpSwapOrder is called, it sets user_info_before_balance = 0
    // to mark the order as consumed (see wrap_up_swap_order.rs line 124)

    if (accountInfo.data.length < 16) {
      // Account exists but data is malformed - treat as exists but not used
      return { exists: true, isUsed: false };
    }

    // Read user_info_before_balance (offset 8, length 8, little-endian u64)
    const userInfoBeforeBalance = accountInfo.data.readBigUInt64LE(8);
    const isUsed = userInfoBeforeBalance === 0n;

    console.log(`[checkSwapOrderStatus] user_info_before_balance: ${userInfoBeforeBalance}`);
    console.log(`[checkSwapOrderStatus] Result: exists=true, isUsed=${isUsed}`);

    return {
      exists: true,
      isUsed,
    };
  } catch (error) {
    console.warn('[checkSwapOrderStatus] Error checking swap order:', error);
    // On error, assume order doesn't exist (proceed with transaction)
    // This is a safe default - worst case we get the SwapOrderUsed error again
    return { exists: false, isUsed: false };
  }
}
